import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';
import { Loader } from './Loader';

const buttonVariants = cva(
  // Base styles - aplicadas a todas las variantes (exactamente como los botones actuales)
  // Agregar estilos para SVGs (iconos) como en shadcn - heredan color y tienen tamaño consistente
  'inline-flex items-center justify-center whitespace-nowrap font-sans font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-current',
  {
    variants: {
      variant: {
        // Primary: Match exacto de PrimaryButton
        primary: [
          'bg-primary',
          'text-primary-foreground',
          'hover:opacity-90',
          'rounded-md',
          'transition-all duration-base ease-smooth',
        ],
        // Ghost: Match exacto de GhostButton
        ghost: [
          'text-foreground', // text-black-1 parece ser text-foreground
          'hover:bg-black/5',
          'rounded-md',
          'transition-colors duration-base ease-smooth',
        ],
        // Secondary: Match exacto de SecondaryButton
        secondary: [
          'text-foreground', // text-black-1
          // Sin padding, sin fondo, sin border
        ],
        // Outline: Nueva variante con borde
        outline: [
          'border border-border',
          'bg-background',
          'text-foreground',
          'hover:opacity-90',
          'rounded-md',
          'transition-colors duration-base ease-smooth',
        ],
        // Destructive: Para acciones destructivas
        destructive: [
          'bg-destructive',
          'text-destructive-foreground',
          'hover:opacity-90',
          'rounded-md',
          'transition-all duration-base ease-smooth',
        ],
        // Link: Estilo de enlace (necesita padding 0)
        link: [
          'text-primary',
          'underline-offset-4',
          'hover:underline',
          'px-0 py-0',
        ],
      },
      size: {
        // Tamaños - padding centralizado aquí para todas las variantes
        sm: [
          'text-sm',
          'px-2 py-1.5',
          'gap-1.5',
        ],
        md: [
          'text-sm',
          'leading-5',
          'px-4 py-3',
          'gap-2',
        ],
        lg: [
          'text-base',
          'px-6 py-3',
          'gap-2.5',
        ],
        icon: [
          'w-10 h-10',
          'p-0',
        ],
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      // Secondary tiene tamaños de texto específicos
      {
        variant: 'secondary',
        size: 'sm',
        class: 'text-xs',
      },
      {
        variant: 'secondary',
        size: 'md',
        class: 'text-sm leading-5',
      },
      {
        variant: 'secondary',
        size: 'lg',
        class: 'text-base',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Contenido del botón
   */
  children: React.ReactNode;
  
  /**
   * Icono a la izquierda del texto
   */
  leftIcon?: React.ReactNode;
  
  /**
   * Icono a la derecha del texto
   */
  rightIcon?: React.ReactNode;
  
  /**
   * Estado de carga. Cuando es true, muestra un spinner y deshabilita el botón
   */
  loading?: boolean;
  
  /**
   * Si es true, el botón ocupa todo el ancho disponible
   */
  fullWidth?: boolean;
  
  /**
   * Si es true, el botón se renderiza como un Slot de Radix UI
   * Útil para renderizar como otro elemento (ej: Link de react-router)
   */
  asChild?: boolean;
  
  /**
   * Tamaño del loader cuando loading es true
   * @default "sm"
   */
  loaderSize?: 'sm' | 'md' | 'lg';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      children,
      leftIcon,
      rightIcon,
      loading = false,
      disabled,
      asChild = false,
      loaderSize = 'sm',
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    // Tamaños del loader - ajustados para que se vean bien en los botones
    const loaderSizes = {
      sm: { width: '12px', height: '12px' },
      md: { width: '16px', height: '16px' },
      lg: { width: '20px', height: '20px' },
    };

    const currentLoaderSize = loaderSizes[loaderSize];

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {/* Loading state */}
        {loading ? (
          <div className="relative" style={{ width: '1em', height: '1em' }}>
            {Array.from({ length: 12 }).map((_, index) => {
              const angle = (index * 30) - 90;
              const opacity = index / 12;
              return (
                <div
                  key={index}
                  className="absolute bg-current rounded-full"
                  style={{
                    width: '7.5%', height: '17.5%',
                    left: '46.25%', top: '5%',
                    transformOrigin: '50% 280%',
                    transform: `rotate(${angle}deg)`,
                    animation: 'spin-bar 1s linear infinite',
                    animationDelay: `${index * (1 / 12)}s`,
                    opacity: 0.2 + opacity * 0.8,
                  }}
                />
              );
            })}
          </div>
        ) : (
          <>
            {leftIcon && (
              <span className="flex items-center justify-center flex-shrink-0">
                {leftIcon}
              </span>
            )}
            <span>{children}</span>
            {rightIcon && (
              <span className="flex items-center justify-center flex-shrink-0">
                {rightIcon}
              </span>
            )}
          </>
        )}

      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
export default Button;

